// The comparator is SHARED (FNP-912/923) — nulls-last in both directions,
// numeric vs string, and empty-string-counts-as-missing live in one place.
// Do not write another one here.
import {useTableSort} from '@flexinet/ui-components';

import {useTestRunsQuery} from './useTestRunsQuery';
import {useTestRunsSearch} from './useTestRunsSearch';
import {useTestRunsTableColumns} from './useTestRunsTableColumns';
// Only the DEFAULT stays local: WHICH key this table sorts by is a per-entity
// judgement, unlike HOW two values compare.
import {DEFAULT_SORT} from '../utils/sortTestRuns';

export const useTestRuns = (filters: Record<string, string> = {}) => {
    const {
        data: items = [],
        isLoading: loading,
        isError,
    } = useTestRunsQuery(filters);
    const {keyword} = useTestRunsSearch();
    const {columns} = useTestRunsTableColumns();

    // Client-side keyword filtering. `keyword` comes from the module-scoped store
    // in useTestRunsSearchQuery, so the value typed into the table toolbar in a
    // sibling component actually reaches this filter (FNP-815).
    const needle = keyword.trim()
                          .toLowerCase();

    const visibleItems = items.filter(item =>
        !needle ||
        item.testCode?.toLowerCase()
            .includes(needle) ||
        // The raw ids stay searchable as a fallback: when testCode was never
        // captured the id IS what is rendered in the column, so matching on it is
        // what makes that row findable at all.
        item.testDefinitionId?.toLowerCase()
            .includes(needle) ||
        item.syntheticUserId?.toLowerCase()
            .includes(needle) ||
        // `claimedBy` is searchable on purpose: chasing "which canary stranded
        // these runs" means pasting one canary id and seeing every run it held.
        // That is the primary ABANDONED triage move (FNP-969).
        item.claimedBy?.toLowerCase()
            .includes(needle) ||
        item.status?.toLowerCase()
            .includes(needle)
    );

    // Sorted AFTER filtering so the ordering describes what is on screen.
    // Sort state is component-level by design (FNP-912): it is how ONE operator
    // is viewing this table, unlike the keyword, which is module-scoped because
    // a sibling toolbar sets it.
    const {
        sortStatus,
        setSortStatus,
        sorted: records,
    } = useTableSort(visibleItems, DEFAULT_SORT);

    return {
        items,
        visibleItems,
        records,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        columns,
    };
};
